import { ArrowsClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowsClockwise";
import { ChatCircleIcon } from "@phosphor-icons/react/dist/csr/ChatCircle";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { dayjs } from "@/core/utils/date";
import { formatByteSize } from "@/profitability/document-chip";
import { MetaList } from "@/profitability/meta-list";
import { shortPeriod } from "@/profitability/new-run/run-period";
import type { ProfitabilityDocument } from "@/profitability/types";
import {
  type DocumentEntry,
  documentRows,
  entryPurpose,
  rowsLabel,
} from "./document-entries";
import { readStatus } from "./document-list-row";
import type { DocumentsRun } from "./documents-tab";
import { useDocumentDownload } from "./use-document-download";

/** "What's in jobs.csv for Oct 12–18?": what Ask Volca opens the chat with. */
export const documentQuestion = (
  fileName: string,
  run: Pick<DocumentsRun, "start_date" | "end_date">,
) =>
  run.start_date && run.end_date
    ? `What's in ${fileName} for ${shortPeriod({ start_date: run.start_date, end_date: run.end_date })}?`
    : `What's in ${fileName}?`;

/**
 * The selected document's name, what it is in a line (what it is used as,
 * its rows and size, who uploaded it and when), and what can be done with
 * it: download the original, replace it for this period, or ask Volca.
 */
export const DocumentHeader = ({
  businessId,
  run,
  entry,
  document,
  replaceHref,
  onAsk,
}: {
  businessId: string;
  run: DocumentsRun;
  entry: DocumentEntry;
  document: ProfitabilityDocument;
  /** The upload page for this slot and period; null when not offered. */
  replaceHref: string | null;
  onAsk?: (question: string) => void;
}) => {
  const { download, downloading } = useDocumentDownload(
    businessId,
    document.id,
  );
  const purpose = entryPurpose(entry);
  const uploaded = dayjs(document.created_at).format("MMM D, YYYY");
  return (
    <Stack
      direction="row"
      gap={4}
      align="start"
      justify="between"
      wrap
      className="min-w-0"
    >
      <Stack gap={1} align="stretch" className="min-w-0 flex-1 basis-80">
        <Text
          variant="heading"
          render={<h2 />}
          className="[overflow-wrap:anywhere]"
        >
          {document.file_name}
        </Text>
        <Text tone="muted">
          <MetaList>
            {purpose &&
              (entry.role === "goal"
                ? "Your numbers for the period"
                : `Used as ${purpose}`)}
            {readStatus(document.status) ??
              (document.kind === "text"
                ? "Read as text"
                : rowsLabel(documentRows(document)))}
            {formatByteSize(document.byte_size)}
          </MetaList>
        </Text>
        <Text variant="caption" tone="muted">
          {document.uploaded_by
            ? `Uploaded by ${document.uploaded_by.full_name} on ${uploaded}`
            : `Uploaded ${uploaded}`}
        </Text>
      </Stack>
      <Stack direction="row" gap={2} wrap className="max-w-full min-w-0">
        <Button variant="secondary" loading={downloading} onClick={download}>
          <DownloadSimpleIcon aria-hidden data-icon="inline-start" />
          Download original
        </Button>
        {replaceHref && (
          <Button
            variant="secondary"
            nativeButton={false}
            render={<Link to={replaceHref} />}
          >
            <ArrowsClockwiseIcon aria-hidden data-icon="inline-start" />
            Replace
          </Button>
        )}
        {onAsk && (
          <Button
            variant="outline"
            onClick={() => onAsk(documentQuestion(document.file_name, run))}
          >
            <ChatCircleIcon aria-hidden data-icon="inline-start" />
            Ask Volca
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
